import {
  Button,
  cn,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Loader,
  Pill,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Spinner,
  Stack,
  toast,
} from '@landler/component-library';
import { useQueryClient } from '@tanstack/react-query';
import { FC, PropsWithChildren, Suspense, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RestApiBadRequestServerError } from '@/api/rest/resources/errors/RestApiBadRequestError';
import {
  Annotation,
  AnnotationCategory,
  AnnotationRating,
  Message,
  PatchOrganizationAnnotationsBody,
} from '@/api/rest/resources/types/conversation';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
  withSuspenseBoundary,
} from '@/components';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { FormRadioGroup, FormRadioItem } from '@/components/widgets/o/Form/FormRadio';
import { useUser } from '@/lib/auth';
import { Logger } from '@/lib/logs/logger';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { isNullish } from '@/utils/booleans';
import { printDDMMYYYYHHMM } from '@/utils/formatting/date';

import { useGlobalAnnotations } from '../hooks/useGlobalAnnotations';
import { groupAnnotationsByMessage, useOrganizationAnnotations } from '../hooks/useOrganizationAnnotations';
import {
  useCreateOrganizationAnnotation,
  useUpdateOrganizationAnnotation,
} from '../hooks/useUpdateOrganizationAnnotations';
import { ACTION_BUTTON_STYLE } from './PromptedActions';

export const AnnotationsPanel = ({
  conversationId,
  selectedMessageId,
  onSelectAnnotation,
  showGlobal = false,
}: {
  conversationId: string | null;
  selectedMessageId: string | null;
  onSelectAnnotation: (message: Message | null) => void;
  showGlobal?: boolean;
}) => {
  const [ratingFilter, setRatingFilter] = useState<AnnotationRating | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<AnnotationCategory | null>(null);

  const AnnotationsFromUser = showGlobal ? GlobalAnnotationsByUser : OrganizationAnnotationsByUser;

  return (
    <div className='flex flex-col h-full'>
      <AnnotationFilterBar
        ratingFilter={ratingFilter}
        onRatingChange={setRatingFilter}
        categoryFilter={categoryFilter}
        onCategoryChange={setCategoryFilter}
      />
      <div className='flex-1 min-h-0 overflow-y-auto'>
        {isNullish(conversationId) ? (
          <AnnotationsFromUser
            selectedMessageId={selectedMessageId}
            onSelectAnnotation={onSelectAnnotation}
            ratingFilter={ratingFilter}
            categoryFilter={categoryFilter}
          />
        ) : (
          <CurrentConversationAnnotations
            conversationId={conversationId}
            selectedMessageId={selectedMessageId}
            onSelectAnnotation={onSelectAnnotation}
            ratingFilter={ratingFilter}
            categoryFilter={categoryFilter}
          />
        )}
      </div>
    </div>
  );
};

const GlobalAnnotationsByUser = withSuspenseBoundary(
  ({
    selectedMessageId,
    onSelectAnnotation,
    ratingFilter,
    categoryFilter,
  }: {
    selectedMessageId: string | null;
    onSelectAnnotation: (message: Message | null) => void;
    ratingFilter: AnnotationRating | null;
    categoryFilter: AnnotationCategory | null;
  }) => {
    const user = useUser();
    const allAnnotations = useGlobalAnnotations({ annotated_by_id: user?.id }).data ?? [];

    return (
      <AnnotationsByUser
        selectedMessageId={selectedMessageId}
        onSelectAnnotation={onSelectAnnotation}
        ratingFilter={ratingFilter}
        categoryFilter={categoryFilter}
        allAnnotations={allAnnotations}
      />
    );
  },
  <div className='flex justify-center p-6'>
    <Spinner size={4} />
  </div>,
);

export const OrganizationAnnotationsByUser = withSuspenseBoundary(
  ({
    selectedMessageId,
    onSelectAnnotation,
    ratingFilter,
    categoryFilter,
  }: {
    selectedMessageId: string | null;
    onSelectAnnotation: (message: Message | null) => void;
    ratingFilter: AnnotationRating | null;
    categoryFilter: AnnotationCategory | null;
  }) => {
    const user = useUser();
    const allAnnotations = useOrganizationAnnotations({ annotated_by_id: user?.id }).data ?? [];

    return (
      <AnnotationsByUser
        selectedMessageId={selectedMessageId}
        onSelectAnnotation={onSelectAnnotation}
        ratingFilter={ratingFilter}
        categoryFilter={categoryFilter}
        allAnnotations={allAnnotations}
      />
    );
  },
  <div className='flex justify-center p-6'>
    <Spinner size={4} />
  </div>,
);

const AnnotationsByUser = ({
  selectedMessageId,
  onSelectAnnotation,
  ratingFilter,
  categoryFilter,
  allAnnotations,
}: {
  selectedMessageId: string | null;
  onSelectAnnotation: (message: Message | null) => void;
  ratingFilter: AnnotationRating | null;
  categoryFilter: AnnotationCategory | null;
  allAnnotations: Annotation[];
}) => {
  const { t } = useTranslation();

  const annotations =
    !ratingFilter && !categoryFilter
      ? allAnnotations
      : allAnnotations.filter((a) => {
          const hasRating = ratingFilter && a.rating === ratingFilter;
          const hasCategory = categoryFilter && a.category === categoryFilter;
          return hasRating || hasCategory || (!ratingFilter && !categoryFilter);
        });

  if (annotations.length === 0) {
    return (
      <span className='typography-body1 text-text-secondary text-center m-16' data-testid='no-user-annotations'>
        {t('global.copilot.annotations.noAnnotations')}
      </span>
    );
  }

  return (
    <div className='flex flex-col divide-y divide-neutral-black-8' data-testid='user-all-annotations'>
      {annotations.map((annotation) => (
        <AnnotationPreview
          key={annotation.id}
          annotation={annotation}
          isSelected={selectedMessageId === annotation.message.id}
          onClick={() =>
            selectedMessageId === annotation.message.id
              ? onSelectAnnotation(null)
              : onSelectAnnotation(annotation.message)
          }
        />
      ))}
    </div>
  );
};

export const CurrentConversationAnnotations = withSuspenseBoundary(
  ({
    conversationId,
    selectedMessageId,
    onSelectAnnotation,
    ratingFilter,
    categoryFilter,
  }: {
    conversationId: string;
    selectedMessageId: string | null;
    onSelectAnnotation: (message: Message | null) => void;
    ratingFilter: AnnotationRating | null;
    categoryFilter: AnnotationCategory | null;
  }) => {
    const { t } = useTranslation();
    const allAnnotations = useOrganizationAnnotations({ conversation_id: conversationId }).data ?? [];

    const conversationAnnotations =
      !ratingFilter && !categoryFilter
        ? allAnnotations
        : allAnnotations.filter((a) => {
            const hasRating = ratingFilter && a.rating === ratingFilter;
            const hasCategory = categoryFilter && a.category === categoryFilter;
            return hasRating || hasCategory || (!ratingFilter && !categoryFilter);
          });

    const annotationsByMessage = groupAnnotationsByMessage(conversationAnnotations);
    const annotations = selectedMessageId
      ? (annotationsByMessage[`${selectedMessageId}`] ?? [])
      : conversationAnnotations;

    if (annotations.length === 0) {
      return (
        <span className='typography-body1 text-text-secondary text-center m-4'>
          {t('global.copilot.annotations.noAnnotations')}
        </span>
      );
    }

    return (
      <div className='flex flex-col divide-y divide-neutral-black-8' data-testid='current-conversation-annotations'>
        {annotations.map((annotation) => (
          <AnnotationPreview
            key={annotation.id}
            annotation={annotation}
            isSelected={selectedMessageId === annotation.message.id}
            onClick={() =>
              selectedMessageId === annotation.message.id
                ? onSelectAnnotation(null)
                : onSelectAnnotation(annotation.message)
            }
          />
        ))}
      </div>
    );
  },
  <div className='flex justify-center p-6'>
    <Spinner size={4} />
  </div>,
);

const AnnotationFilterBar = ({
  ratingFilter,
  onRatingChange,
  categoryFilter,
  onCategoryChange,
}: {
  ratingFilter: AnnotationRating | null;
  onRatingChange: (rating: AnnotationRating | null) => void;
  categoryFilter: AnnotationCategory | null;
  onCategoryChange: (category: AnnotationCategory | null) => void;
}) => {
  const { t } = useTranslation();

  return (
    <div className='flex items-center gap-2 px-3 py-2 border-b border-neutral-black-8 shrink-0 bg-white'>
      <Button
        variant='text'
        className={cn(
          'px-2 py-1 rounded-md text-text-secondary hover:text-text-secondary',
          ratingFilter === AnnotationRating.positive
            ? 'bg-success-10 text-success'
            : 'text-text-secondary hover:text-success',
        )}
        onClick={() => onRatingChange(ratingFilter === AnnotationRating.positive ? null : AnnotationRating.positive)}
        leftAdornment={<Icon icon='ri-thumb-up-line' className='text-lg' />}
        title={t('global.copilot.annotations.ratings.positive')}
      />
      <Button
        variant='text'
        className={cn(
          'px-2 py-1 rounded-md text-text-secondary hover:text-text-secondary',
          ratingFilter === AnnotationRating.negative
            ? 'bg-error-10 text-error'
            : 'text-text-secondary hover:text-error',
        )}
        onClick={() => onRatingChange(ratingFilter === AnnotationRating.negative ? null : AnnotationRating.negative)}
        leftAdornment={<Icon icon='ri-thumb-down-line' className='text-lg' />}
        title={t('global.copilot.annotations.ratings.negative')}
      />
      <Select
        value={categoryFilter ?? 'all'}
        onValueChange={(value) => onCategoryChange(value !== 'all' ? (value as AnnotationCategory) : null)}
      >
        <SelectTrigger className='flex-1 min-w-0 text-xs rounded-md border border-neutral-black-12 px-2 py-1 bg-white text-text-secondary focus:outline-none focus:border-neutral-black-12'>
          {(categoryFilter as keyof typeof AnnotationCategory)
            ? t(
                `global.copilot.annotations.form.negative.category.options.${categoryFilter as keyof typeof AnnotationCategory}`,
              )
            : t('global.copilot.annotations.form.negative.category.allCategories')}
        </SelectTrigger>
        <SelectContent>
          <SelectItem key='all' value='all'>
            {t('global.copilot.annotations.form.negative.category.allCategories')}
          </SelectItem>
          {Object.values(AnnotationCategory).map((category) => (
            <SelectItem key={category} value={category}>
              {t(
                `global.copilot.annotations.form.negative.category.options.${category as keyof typeof AnnotationCategory}`,
              )}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

const AnnotationPreview = ({
  annotation,
  isSelected,
  onClick,
}: {
  annotation: Annotation;
  isSelected: boolean;
  onClick: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <Button
      key={annotation.id}
      variant='outline'
      className={cn(
        ACTION_BUTTON_STYLE,
        'bg-white-100 p-3 rounded-none flex flex-col w-full gap-1 justify-start',
        isSelected ? 'bg-primary-12' : '',
      )}
      onClick={onClick}
    >
      <Stack className='w-full flex-row justify-between items-center gap-4'>
        <Stack className='flex flex-row gap-1 items-center justify-center'>
          <span className='typography-caption text-text-primary font-semibold'>{annotation.annotated_by_email}</span>
          <Icon
            icon={annotation.rating === AnnotationRating.positive ? 'ri-thumb-up-line' : 'ri-thumb-down-line'}
            className={cn(
              'text-lg p-0',
              annotation.rating === AnnotationRating.positive ? 'text-success' : 'text-error',
            )}
          />
        </Stack>
        <span className='typography-caption text-text-secondary'>{printDDMMYYYYHHMM(annotation.created_at) ?? ''}</span>
      </Stack>
      <Stack className='w-full gap-4 flex justify-start items-start'>
        {annotation.rating === AnnotationRating.negative && !isNullish(annotation.category) && (
          <Pill size='small' className='typography-caption text-text-secondary bg-divider'>
            {t(
              `global.copilot.annotations.form.negative.category.options.${annotation.category as keyof typeof AnnotationCategory}`,
            )}
          </Pill>
        )}
        <span className='max-w-sm typography-caption text-text-secondary text-start text-ellipsis'>
          {annotation.comment}
        </span>
      </Stack>
    </Button>
  );
};

export const ChatMessageAnnotation = ({ message }: { message: Message }) => {
  const { t } = useTranslation();

  const [rating, setRating] = useState<AnnotationRating | null>(null);
  return (
    <>
      <Stack className='flex flex-row justify-start items-baseline'>
        <Button
          leftAdornment={<Icon icon='ri-thumb-up-line' className='text-lg' />}
          title={t(`global.copilot.annotations.ratings.${AnnotationRating.positive}`)}
          variant='text'
          className='px-2 py-1 rounded-md text-text-secondary hover:text-text-secondary'
          onClick={() => setRating(AnnotationRating.positive)}
        />
        <Button
          rightAdornment={<Icon icon='ri-thumb-down-line' className='text-lg' />}
          title={t(`global.copilot.annotations.ratings.${AnnotationRating.negative}`)}
          variant='text'
          className='px-2 py-1 rounded-md text-text-secondary hover:text-text-secondary'
          onClick={() => setRating(AnnotationRating.negative)}
        />
      </Stack>
      <Dialog open={!isNullish(rating)} onOpenChange={(open) => !open && setRating(null)}>
        <DialogContent className='px-8 py-6' onClick={(e) => e.stopPropagation()} withXButton={false}>
          <DialogHeader>
            <DialogTitle>
              {!isNullish(rating) &&
                t(`global.copilot.annotations.form.${rating as keyof typeof AnnotationRating}.title`)}
            </DialogTitle>
          </DialogHeader>
          <Suspense fallback={<Loader className='min-h-fit' />} key={message.id}>
            {!isNullish(rating) && (
              <AnnotationForm message={message} rating={rating} onSuccess={() => setRating(null)} />
            )}
          </Suspense>
        </DialogContent>
      </Dialog>
    </>
  );
};

type AnnotationFormFields = PatchOrganizationAnnotationsBody;

const AnnotationForm = ({
  message,
  rating,
  onSuccess,
}: {
  message: Message;
  rating: AnnotationRating;
  onSuccess: () => void;
}) => {
  const { t } = useTranslation();
  const organizationId = useOrganizationId();
  const user = useUser();

  const annotationFilters = {
    conversation_id: message.conversation_id,
    message_id: message.id,
    annotated_by_id: user?.id,
  };
  const annotation =
    useOrganizationAnnotations(annotationFilters).data?.find(
      (ann) => ann.message.id === message.id && ann.rating === rating,
    ) ?? null;

  const form = useForm<AnnotationFormFields>({
    defaultValues: {
      comment: null,
      category: null,
      ...(annotation || {}),
      rating,
    },
  });

  const queryClient = useQueryClient();

  const { submit: createSubmit, isSubmitting: createIsSubmitting } = useCreateOrganizationAnnotation();
  const { submit: updateSubmit, isSubmitting: updateIsSubmitting } = useUpdateOrganizationAnnotation();
  const submit = isNullish(annotation) ? createSubmit : updateSubmit;
  const isSubmitting = isNullish(annotation) ? createIsSubmitting : updateIsSubmitting;

  const onSubmit: SubmitHandler<AnnotationFormFields> = async (inputs) => {
    try {
      form.clearErrors();
      await submit(organizationId, message.conversation_id, message.id, { ...(annotation || {}), ...inputs });
      onSuccess();
      queryClient.invalidateQueries({
        queryKey: ['organization', { organization_id: organizationId }, 'annotations'],
      });

      form.reset();
    } catch (error) {
      if (error instanceof Error) {
        Logger.error(error);
      }
      if (error instanceof RestApiBadRequestServerError && error.formErrors.length > 0) {
        error.formErrors.forEach((formError) => {
          form.setError(formError.field as keyof AnnotationFormFields, {
            type: 'custom',
            message: formError.messages?.[0],
          });
        });
      } else {
        toast({
          title: t('global.ui.toast.errorToastFallbackTitle'),
          description: t('global.ui.toast.errorToastFallbackDescription'),
          type: 'error',
        });
      }
    }
  };

  return (
    <FormProvider {...form}>
      <DialogBody>
        <form id='message-annotation-form' onSubmit={form.handleSubmit(onSubmit)} data-testid='message-annotation-form'>
          <fieldset disabled={isSubmitting}>
            <Stack className='gap-4'>
              {rating === AnnotationRating.negative && (
                <FormField
                  control={form.control}
                  name='category'
                  rules={{ required: false }}
                  render={() => (
                    <FormItem className='flex flex-col gap-2'>
                      <CustomFormLabel>{t('global.copilot.annotations.form.negative.category.label')}</CustomFormLabel>
                      <FormControl>
                        <FormRadioGroup name='category' className='px-4 py-2' controlsform={form}>
                          {Object.keys(AnnotationCategory).map((category) => (
                            <FormRadioItem
                              key={category}
                              label={t(
                                `global.copilot.annotations.form.negative.category.options.${category as keyof typeof AnnotationCategory}`,
                              )}
                              value={category}
                            />
                          ))}
                        </FormRadioGroup>
                      </FormControl>
                      <CustomFormMessage>{form.formState.errors.category?.message}</CustomFormMessage>
                    </FormItem>
                  )}
                />
              )}
              <FormField
                control={form.control}
                name='comment'
                rules={{ required: false }}
                render={({ field: { value, ...field } }) => (
                  <FormItem className='flex flex-col gap-2'>
                    <CustomFormLabel>
                      {t(`global.copilot.annotations.form.${rating as keyof typeof AnnotationRating}.comment.label`)}
                    </CustomFormLabel>
                    <FormControl>
                      <textarea
                        className={cn(
                          'flex min-h-[200px] w-full rounded-lg border border-neutral-black-12 p-4 placeholder:text-text-disabled',
                          'disabled:cursor-not-allowed disabled:border-text-disabled',
                        )}
                        placeholder={value ?? ''}
                        value={value ?? ''}
                        {...field}
                      />
                    </FormControl>
                    <CustomFormMessage>{form.formState.errors.comment?.message}</CustomFormMessage>
                  </FormItem>
                )}
              />
            </Stack>
          </fieldset>
        </form>
      </DialogBody>
      <DialogFooter className='mt-6'>
        <DialogClose asChild>
          <Button variant='text' size='small' disabled={isSubmitting}>
            {t('global.ui.buttons.cancel')}
          </Button>
        </DialogClose>
        <Stack direction='row' spacing={4}>
          <Button
            form='message-annotation-form'
            size='small'
            type='submit'
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            {t('global.ui.buttons.save')}
          </Button>
        </Stack>
      </DialogFooter>
    </FormProvider>
  );
};

const CustomFormLabel: FC<PropsWithChildren> = ({ children }) => {
  const { error } = useFormField();

  return (
    <FormLabel
      className={cn('text-[.75rem] font-medium leading-4 text-text-secondary md:text-[.8125rem] md:leading-5', {
        'text-error': error,
      })}
    >
      {children}
    </FormLabel>
  );
};

const CustomFormMessage: FC<PropsWithChildren> = ({ children }) => (
  <FormMessage className='typography-inputLabel mt-0 text-error'>{children}</FormMessage>
);
